import type { HyphenationRules, WordLanguages } from './hyphenate';
import { LineBuilder, measureBreakWidth, type MeasureTextWidth, type WrappedLine } from './wrap-lines';

/**
 * Горизонтальный интервал, доступный строке внутри area-контура (Photoshop Area Type on Shape).
 * Координаты — canvas-px относительно rect бокса (x0 = отступ строки от левого края rect).
 */
export interface AreaLineSpan {
  x0: number;
  width: number;
}

type Point = readonly [number, number];

interface ContourSegment {
  kind: 'line' | 'cubic';
  points: Point[]; // line: [p0, p1]; cubic: [p0, c1, c2, p3]
}

/** Число шагов флэттенинга кубического сегмента. Зафиксировано для TS↔Rust parity — не менять в одиночку. */
export const AREA_SHAPE_FLATTEN_STEPS = 24;

/**
 * Доли высоты ЧЕРНИЛЬНОЙ полосы строки (не строчной!), в которых сэмплируется контур.
 * Зафиксировано для TS↔Rust parity.
 */
export const AREA_SHAPE_BAND_SAMPLES: readonly number[] = [0.02, 0.5, 0.98];

/** Доля высоты чернил для запасного сэмпла, когда пересечение всех сэмплов пусто. TS↔Rust parity. */
export const AREA_SHAPE_BAND_CENTER_SAMPLE = 0.5;

/** Потолок числа строчных полос — защита от вырожденного lineHeight. */
const MAX_BANDS = 5000;

/**
 * Парсер нормированного контура `TextAreaShapeParams.d`: абсолютные M/L/C/Z, один subpath.
 * Не общий SVG-парсер: принимает ровно то, что пишет psd-parser (`buildNormalizedAreaShapeD`).
 */
function parseAreaShapeD(d: string): ContourSegment[] {
  const tokens = d.trim().split(/[\s,]+/);
  const segments: ContourSegment[] = [];
  let cursor: Point | undefined;
  let start: Point | undefined;
  let i = 0;

  const num = (): number => {
    const v = Number(tokens[i]);
    i += 1;
    return Number.isFinite(v) ? v : 0;
  };

  while (i < tokens.length) {
    const cmd = tokens[i];
    i += 1;
    switch (cmd) {
      case 'M': {
        cursor = [num(), num()];
        start = cursor;
        break;
      }

      case 'L': {
        const p: Point = [num(), num()];
        if (cursor) segments.push({ kind: 'line', points: [cursor, p] });
        cursor = p;
        break;
      }

      case 'C': {
        const c1: Point = [num(), num()];
        const c2: Point = [num(), num()];
        const p: Point = [num(), num()];
        if (cursor) segments.push({ kind: 'cubic', points: [cursor, c1, c2, p] });
        cursor = p;
        break;
      }

      case 'Z':
      case 'z': {
        if (cursor && start && (cursor[0] !== start[0] || cursor[1] !== start[1])) {
          segments.push({ kind: 'line', points: [cursor, start] });
        }

        cursor = start;
        break;
      }

      default:
        // Незнакомый токен — контур считаем невалидным, чтобы не «переносить» по мусору.
        return [];
    }
  }

  return segments;
}

function cubicAt(p0: Point, c1: Point, c2: Point, p3: Point, t: number): Point {
  const u = 1 - t;
  return [
    u * u * u * p0[0] + 3 * u * u * t * c1[0] + 3 * u * t * t * c2[0] + t * t * t * p3[0],
    u * u * u * p0[1] + 3 * u * u * t * c1[1] + 3 * u * t * t * c2[1] + t * t * t * p3[1],
  ];
}

/** Контур → замкнутый полигон (флэттенинг кубик фиксированным шагом). */
function flattenContour(segments: ContourSegment[]): Point[] {
  const poly: Point[] = [];
  for (const seg of segments) {
    if (seg.kind === 'line') {
      poly.push(seg.points[0]);
      poly.push(seg.points[1]);
    } else {
      const [p0, c1, c2, p3] = seg.points;
      for (let s = 0; s <= AREA_SHAPE_FLATTEN_STEPS; s++) {
        poly.push(cubicAt(p0, c1, c2, p3, s / AREA_SHAPE_FLATTEN_STEPS));
      }
    }
  }

  return poly;
}

interface Interval {
  a: number;
  b: number;
}

/** Even-odd пересечения горизонтали y с полигоном → отсортированные интервалы «внутри». */
function intervalsAtY(poly: Point[], y: number): Interval[] {
  const xs: number[] = [];
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % poly.length];
    if (p[1] === q[1]) continue;
    const crosses = p[1] > y !== q[1] > y;
    if (!crosses) continue;
    xs.push(p[0] + ((y - p[1]) * (q[0] - p[0])) / (q[1] - p[1]));
  }

  xs.sort((a, b) => a - b);
  const out: Interval[] = [];
  for (let i = 0; i + 1 < xs.length; i += 2) out.push({ a: xs[i], b: xs[i + 1] });
  return out;
}

function intersectIntervalLists(list1: Interval[], list2: Interval[]): Interval[] {
  const out: Interval[] = [];
  for (const u of list1) {
    for (const v of list2) {
      const a = Math.max(u.a, v.a);
      const b = Math.min(u.b, v.b);
      if (b > a) out.push({ a, b });
    }
  }

  return out;
}

/**
 * Пер-строчные интервалы area-контура для равномерной базлайн-сетки.
 *
 * Строка i занимает полосу [i·lineHeightPx, (i+1)·lineHeightPx], но чернила строки лежат НЕ по всей
 * полосе: раскладка ставит базлайн в `bandTop + inkHeightPx` (cap-height), а верх кегля — ровно в
 * `bandTop`. Поэтому сэмплируем контур в {@link AREA_SHAPE_BAND_SAMPLES} долях ЧЕРНИЛЬНОЙ полосы
 * [bandTop, bandTop + inkHeightPx], а не строчной: доли от lineHeight смещали бы окно вниз на
 * (lineHeight − capHeight) — верх глифов оказывался бы вне контура (круг), а по диагонали строка
 * получала бы фантомный отступ. Интервалы «внутри» пересекаются между сэмплами (консервативно —
 * строка помещается на всей высоте своих чернил), берётся самый широкий. Выносные элементы (ниже
 * базлайна) не сэмплируются: их глубина известна только после переноса — компромисс MVP.
 * Photoshop при нескольких «островах» на строке льёт текст по всем — MVP берёт один
 * широчайший (для несвязных контуров текст займёт больший остров).
 *
 * @param d нормированный контур (см. TextAreaShapeParams)
 * @param rectWidth/rectHeight размеры rect бокса в canvas-px
 * @param lineHeightPx шаг базлайн-сетки в canvas-px
 * @param inkHeightPx высота чернил строки от верха полосы (cap-height раскладки); `<= 0` → вся полоса
 * @returns span на каждую полосу внутри rect; `null` = полоса вне контура (строка пропускается).
 *          Индексы за пределами массива = ниже контура (caller переходит на rect-wrap overflow).
 *          Пустой список = контур непригоден (мусорный `d`, вырожденные размеры, ни одной живой
 *          полосы) — caller обязан остаться на обычном rect-wrap.
 */
export function buildAreaShapeLineSpans(
  d: string,
  rectWidth: number,
  rectHeight: number,
  lineHeightPx: number,
  inkHeightPx: number,
): (AreaLineSpan | null)[] {
  if (!(rectWidth > 0) || !(rectHeight > 0) || !(lineHeightPx > 0)) return [];
  const segments = parseAreaShapeD(d);
  if (segments.length === 0) return [];
  const poly = flattenContour(segments).map((p): Point => [p[0] * rectWidth, p[1] * rectHeight]);

  // Нет cap-метрики (вырожденный шрифт) — сэмплируем всю полосу: хуже, но не вырождается в точку.
  const inkPx = inkHeightPx > 0 ? inkHeightPx : lineHeightPx;
  const bandCount = Math.min(MAX_BANDS, Math.ceil(rectHeight / lineHeightPx));
  const spans: (AreaLineSpan | null)[] = [];
  let liveBands = 0;
  for (let i = 0; i < bandCount; i++) {
    const bandTop = i * lineHeightPx;
    let acc: Interval[] | undefined;
    for (const f of AREA_SHAPE_BAND_SAMPLES) {
      const cur = intervalsAtY(poly, bandTop + f * inkPx);
      acc = acc === undefined ? cur : intersectIntervalLists(acc, cur);
      if (acc.length === 0) break;
    }

    // Пустое пересечение сэмплов ≠ полоса вне контура: у наклонного узкого контура (лента,
    // «ромб») интервалы сэмплов не перекрываются, хотя место в полосе есть. Иначе мертва каждая
    // полоса и весь параграф уезжает ниже rect. Центр чернил — последнее слово.
    if (acc === undefined || acc.length === 0) {
      acc = intervalsAtY(poly, bandTop + AREA_SHAPE_BAND_CENTER_SAMPLE * inkPx);
    }

    let widest: Interval | undefined;
    for (const iv of acc) {
      if (!widest || iv.b - iv.a > widest.b - widest.a) widest = iv;
    }

    if (widest) liveBands += 1;
    spans.push(widest ? { x0: widest.a, width: widest.b - widest.a } : null);
  }

  // Ни одной живой полосы — заливать текст некуда; пустой список держит caller на rect-wrap.
  return liveBands > 0 ? spans : [];
}

/**
 * Вариант {@link wrapLines} для area-контура: доступная ширина зависит от индекса строки.
 * Алгоритм переноса (UAX#14-lite сегменты + greedy + break-word) идентичен `wrapLines`.
 *
 * `lineWidthAt(i) <= 0` — полоса вне контура: строка выводится пустой, текст не расходуется.
 * Явный `\n` всегда завершает текущую строку.
 *
 * Если положительной ширины нет ни в одной полосе окна — переносить некуда, строки отдаются как
 * есть (паритет `wrapLines(maxWidth <= 0)`), иначе получили бы MAX_BANDS пустых строк.
 */
export function wrapLinesShaped(
  rawLines: readonly string[],
  measureWidth: MeasureTextWidth,
  lineWidthAt: (lineIndex: number) => number,
): string[] {
  return wrapLinesShapedDetailed(rawLines, measureWidth, lineWidthAt).map(line => line.text);
}

/** {@link wrapLinesShaped} с признаком конца абзаца на каждой строке — см. {@link WrappedLine}. */
export function wrapLinesShapedDetailed(
  rawLines: readonly string[],
  measureWidth: MeasureTextWidth,
  lineWidthAt: (lineIndex: number) => number,
  hyphenation?: HyphenationRules,
  languages?: WordLanguages,
): WrappedLine[] {
  let hasLiveBand = false;
  for (let i = 0; i < MAX_BANDS; i++) {
    if (lineWidthAt(i) > 0) {
      hasLiveBand = true;
      break;
    }
  }

  if (!hasLiveBand) return rawLines.map(text => ({ text, paragraphEnd: true }));

  // Полосы вне контура пропускаем до первой пригодной (конечность гарантирована: за пределами spans caller
  // отдаёт rect-ширину > 0). Мёртвые полосы — пустые строки без дефиса: предел подряд идущих переносов они сбрасывают.
  const skipDeadBands = (): void => {
    while (lineWidthAt(builder.lines.length) <= 0 && builder.lines.length < MAX_BANDS) builder.push('', false);
  };

  const builder: LineBuilder = new LineBuilder(text => measureWidth(text), hyphenation, skipDeadBands, languages);
  const result = builder.lines;

  const maxWidth = (): number => lineWidthAt(result.length);

  for (const rawLine of rawLines) {
    const segments = rawLine.split(/(?<=[\s\-/‐‒–—])|(?=—)/);
    skipDeadBands();
    let currentLine = '';

    for (const segment of segments) {
      builder.segEnd += segment.length;
      const testLine = currentLine + segment;

      if (measureBreakWidth(testLine, measureWidth) > maxWidth() && currentLine.length > 0) {
        currentLine = builder.breakBefore(currentLine, segment, maxWidth());
      } else {
        currentLine = testLine;
      }

      // Ширина полосы меняется от строки к строке — перебор идёт по живому индексу результата.
      currentLine = builder.breakOverflow(currentLine, maxWidth);
    }

    builder.push(currentLine, true);
    builder.segEnd += 1;
  }

  return result;
}
